import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { FaShoppingBag, FaTruckMoving, FaUtensils } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  addCartDetails,
  changeOrderType,
  clearCartDetails,
} from "../store/actions/cartAction";
import { getRestaurantsAction } from "../store/actions/restaurantsAction";
import MainNavigation from "../components/MainNavigation";
import { isWithinTimeRange } from "../utils/isWithinTimeRange";
import { getNextStoreOpen } from "../utils/getNextStoreOpen";
import RestaurantLoader from "../utils/RestaurantLoader";
import { colorToggle } from "../store/actions/GlobalAction";
import { toast } from "react-toastify";

const OrderOptions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { restaurantsDetail, getLoader } = useSelector(
    (state) => state.restaurants
  );
  const { cartDetails, allTablesData } = useSelector((state) => state.cart);
  const [selectedOption, setSelectedOption] = useState("");
  const { uid } = useSelector((state) => state.authUser);
  const [nextOpenInfo, setNextOpenInfo] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const { color } = useSelector((state) => state.global);
  const routeId = cartDetails?.restaurantId;
  const isStoreDineInOpen = isWithinTimeRange(restaurantsDetail?.storeTiming);

  const checkDineInAvailability = () => {
    const allTables = Object.values(allTablesData?.allTables || {});
    const currentTime = new Date().getTime();
    const fiveMinutes = 5 * 60 * 1000;

    return allTables.some((table) => {
      const isUnoccupied = !table.customerId;

      const isOccupiedByCurrentUser =
        table.customerId === uid &&
        cartDetails?.restaurantId === restaurantsDetail?.id;

      let isExpired = false;
      if (table.expireAt?.seconds) {
        const expireAtMs =
          table.expireAt.seconds * 1000 +
          Math.floor(table.expireAt.nanoseconds / 1e6);
        isExpired = currentTime - expireAtMs > fiveMinutes;
      }

      return isUnoccupied || isExpired || isOccupiedByCurrentUser;
    });
  };

  const isThereAnyTableExist = allTablesData?.allTables
    ? Object.keys(allTablesData?.allTables || {}).length > 0
    : false;
  const isAnyTableAvailable = checkDineInAvailability();
  const isDineInAvailable = isStoreDineInOpen;

  const isDeliveryAvailable = isWithinTimeRange(
    restaurantsDetail?.deliveryDays
  );
  const isTakeawayAvailable = isWithinTimeRange(restaurantsDetail?.pickupDays);

  const options = [
    {
      key: "dinein",
      label: "Dine In",
      icon: FaUtensils,
      text: "Enjoy your meal at our restaurant with cozy seating.",
      available: isDineInAvailable,
    },
    {
      key: "delivery",
      label: "Home Delivery",
      icon: FaTruckMoving,
      text: "Get your order delivered hot and fresh to your home.",
      available: isDeliveryAvailable && isDineInAvailable,
    },
    {
      key: "takeaway",
      label: "Take Away",
      icon: FaShoppingBag,
      text: "Pick up your order yourself and enjoy it anywhere you like.",
      available: isTakeawayAvailable && isDineInAvailable,
    },
  ];

  const handleCardClick = (key) => {
    setSelectedOption(key);
  };

  const handleNavigate = () => {
    if (!routeId) return;
    setButtonLoading(true);
    if (selectedOption === "dinein") {
      let message = null;

      if (!isThereAnyTableExist) {
        message =
          "No tables are available right now for dine-in at this restaurant. Please choose a different ordering option at this time.";
      } else if (!isAnyTableAvailable) {
        message =
          "All tables are currently reserved. Please choose a different ordering option at this time.";
      }

      if (message) {
        toast.warning(message);
        setButtonLoading(false);
        return;
      }
    }

    const existingCart = cartDetails || {};
    const updatedCart = {
      ...existingCart,
      orderType: selectedOption,
    };
    dispatch(addCartDetails(updatedCart));
    dispatch(changeOrderType(selectedOption));
    setTimeout(() => {
      history.push(`/restaurant/${routeId}`);
    }, 1000);
  };

  useEffect(() => {
    if (!routeId) {
      dispatch(clearCartDetails());
      history.push("/restaurants");
      return;
    }
    dispatch(getRestaurantsAction(routeId));
  }, [dispatch, history, routeId]);

  useEffect(() => {
    const color = restaurantsDetail?.customerColor;
    if (color) {
      localStorage.setItem("mainColor", color);
      dispatch(colorToggle(color));
    }
  }, [dispatch, restaurantsDetail]);

  useEffect(() => {
    if (!isDineInAvailable) {
      const nextOpen = getNextStoreOpen(restaurantsDetail?.storeTiming);
      setNextOpenInfo(nextOpen ? `We’ll reopen on ${nextOpen}.` : "");
    } else {
      setNextOpenInfo("");
    }
  }, [isDineInAvailable, restaurantsDetail]);

  return (
    <section className="container py-5">
      {routeId ? (
        <>
          <MainNavigation title="How would you like to order?" />
          <div className="row justify-content-center g-4 px-5">
            {getLoader ? (
              <div className="text-center py-4">
                <RestaurantLoader message="Fetching Order Options" />
              </div>
            ) : (
              <>
                {options.some((opt) => opt.available) ? (
                  options
                    .filter((opt) => opt.available)
                    .map(({ key, icon: Icon, label, text }) => (
                      <div key={key} className="col-md-4 col-sm-12 my-3">
                        <div
                          className={`card text-center h-100 shadow-lg p-4 ${
                            selectedOption === key ? "selected" : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.3s",
                            transform:
                              selectedOption === key
                                ? "scale(1.08)"
                                : "scale(1)",
                            backgroundColor:
                              selectedOption === key ? "#f8f9fa" : "#fff",
                            border:
                              selectedOption === key
                                ? `4px solid ${color}`
                                : "1px solid #dee2e6",
                            borderRadius: "16px",
                          }}
                          onClick={() => handleCardClick(key)}
                        >
                          <div className="mb-3">
                            <Icon size={40} color={color} />
                          </div>
                          <h5 className="card-title mb-2">{label}</h5>
                          <p className="card-text text-muted">{text}</p>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="text-center">
                    <div
                      className="d-inline-block p-4 px-5 rounded"
                      style={{
                        backgroundColor: "#f8d7da",
                        color: "#721c24",
                        fontWeight: "bold",
                        fontSize: "1.5rem",
                        border: "2px solid #dc3545",
                        borderRadius: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      }}
                    >
                      🚫 Store is Closed Now
                      {nextOpenInfo && (
                        <div
                          className="mt-2"
                          style={{ fontSize: "1.1rem", fontWeight: "normal" }}
                        >
                          {nextOpenInfo}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {selectedOption && (
            <div className="text-center mt-5">
              <button
                className="btn btn-lg rounded-pill px-4 py-2 fw-semibold"
                style={{
                  backgroundColor: color,
                  color: "white",
                  boxShadow: `0 4px 10px ${color}80`,
                  transition: "all 0.3s ease-in-out",
                }}
                onClick={handleNavigate}
                disabled={buttonLoading}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Proceed
              </button>
            </div>
          )}
        </>
      ) : (
        history?.push("/restaurants")
      )}
    </section>
  );
};

export default OrderOptions;
